<template>
    <div
        ref="container"
        :class="{
            [props.emptyClass || '']: props.emptyClass && !isBannerVisible && !slotConfig.placeholderHeight,
            [props.backgroundClass || '']: props.backgroundClass && (!adDisabledStore.isAdDisabled || showAdFreeBanner),
        }"
        :style="{
            '--slot-max-height': slotHeight ? `${slotHeight}px` : 'auto',
        }"
    >
        <div
            :class="$style.wrapper"
            :style="{
                '--placeholder-height': slotConfig.placeholderHeight ? `${slotConfig.placeholderHeight}px` : 'auto',
            }"
        >
            <AdFreeBanner
                v-if="showAdFreeBanner"
                :type="type"
            />
            <div
                v-else
                :id="id"
                :class="$style.container"
            />
            <slot :isVisible="isBannerVisible" />
        </div>
    </div>
</template>
<script lang="ts" setup>
import { useResizeObserver, useThrottleFn } from '@vueuse/core'
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, useId, useTemplateRef, watch } from 'vue'

import AdFreeBanner from '@/components_new/AdFreeBanner/AdFreeBanner.vue'
import { EXISTING_AD_FREE_BANNERS } from '@/components_new/AdFreeBanner/banners'
import type { PublicPageAdBanner } from '@/modules/adv/public-page-ad-banner'
import type { PageAdSize, PageAdType } from '@/modules/adv/types'
import { useAdDisabledStore } from '@/store/ad-disabled-store'
import { useAdsStore } from '@/store/ads-store'
import { idleCallbackPromise } from '@/utils/helpers'

import { bannersConfig } from './config'

const adsStore = useAdsStore()
const adDisabledStore = useAdDisabledStore()

type Props = {
    type: PageAdType
    /**
     * Class to add when the ad is not loaded
     * Should not hide element with "display: none", otherwise the ad will never be loaded
     */
    emptyClass?: string
    backgroundClass?: string
    respectHeight?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
    (e: 'update:visible', value: boolean): void
}>()
const slotConfig = computed(() => bannersConfig[props.type])

const showAdFreeBanner = computed(
    () => adDisabledStore.isAdFreeActive && EXISTING_AD_FREE_BANNERS[props.type] !== undefined,
)

const slotHeight = computed(() => {
    const heights = slotConfig.value.sizes.filter((s) => s !== 'fluid').map(([, h]) => h)
    return heights.length ? Math.max(...heights) : undefined
})

// get sorted sizes in descending width order, "fluid" is always the last — it can be any size
const sortedSizesDesc = computed(() =>
    slotConfig.value.sizes.slice().sort((a, b) => {
        if (a === 'fluid') {
            return 1
        }
        if (b === 'fluid') {
            return -1
        }
        return b[0] - a[0]
    }),
)

const banner = shallowRef<PublicPageAdBanner>()
const isBannerVisible = ref(false)

const id = useId()
const containerRef = useTemplateRef('container')
const lastFilteredSizes = ref<PageAdSize[]>()

async function updateBanner() {
    await idleCallbackPromise()

    if (!adsStore.adService || !containerRef.value || adDisabledStore.isAdDisabled) {
        return
    }

    const rect = containerRef.value.getBoundingClientRect()
    const filteredSizes = sortedSizesDesc.value.filter((size) => {
        if (size === 'fluid') {
            return rect.width > 0
        }
        const heightFits = !props.respectHeight || rect.height >= size[1]
        return rect.width >= size[0] && heightFits
    })
    // do nothing, if the max size is the same
    if (JSON.stringify(filteredSizes) === JSON.stringify(lastFilteredSizes.value)) {
        return
    }

    lastFilteredSizes.value = filteredSizes

    // destroy the previous banner
    banner.value?.destroy()
    banner.value = undefined

    // prepare a new banner if there are sizes
    if (filteredSizes.length > 0 && rect.width > 0) {
        banner.value = adsStore.adService.requestPageAd({
            type: props.type,
            refresh: slotConfig.value.refreshInterval,
            el: id,
            sizes: filteredSizes,
        })

        banner.value.addEventListener('rendered', () => {
            isBannerVisible.value = true
            emit('update:visible', true)
        })
        banner.value.addEventListener('empty', () => {
            isBannerVisible.value = false
            emit('update:visible', false)
        })
    }
}

const updateBannerThrottled = useThrottleFn(updateBanner, 1000)

async function checkEmptyStyles() {
    if (!props.emptyClass) {
        return
    }

    await idleCallbackPromise()

    const elem = containerRef.value?.appendChild(document.createElement('div'))
    if (elem) {
        elem.classList.add(props.emptyClass)
        if (window.getComputedStyle(elem).display === 'none') {
            console.error(
                `AdBlock: class .${props.emptyClass} sets display to "none". That breaks tracking of the future size changes.\n\nUse negative margins to hide empty ad in the layout.`,
            )
        }
        elem.remove()
    }
}

onMounted(() => {
    checkEmptyStyles()
    updateBannerThrottled()
})

watch(
    () => props.emptyClass,
    () => {
        checkEmptyStyles()
    },
)

watch(
    () => adDisabledStore.isAdDisabled,
    (value) => {
        // destroy the previous banner
        banner.value?.destroy()
        banner.value = undefined
        if (!value) {
            updateBannerThrottled()
        }
    },
)

useResizeObserver(containerRef, updateBannerThrottled)

onBeforeUnmount(() => {
    banner.value?.destroy()
})
</script>

<style module>
.wrapper {
    width: fit-content;
    margin: 0 auto;
    position: relative;
    pointer-events: none;
    min-height: var(--placeholder-height);
    max-height: var(--slot-max-height);
}

/* fluid ads has inline style with "width: 100%". In that case wrapper should also take all space */
.wrapper:has(div[style*='width: 100%;']) {
    width: 100%;
}

.container {
    /* TODO: check ad requirements and remove if not needed */
    min-width: 1px;
    min-height: 1px;
    pointer-events: auto;
    overflow: hidden;
    max-height: var(--slot-max-height);
}
</style>
